import classNames from 'classnames'
import React, {Component, useEffect, useRef, useState, useCallback} from 'react'
import {Group, Image, Layer, Stage, Text} from 'react-konva'
import {Button, Input, InputGroup, UncontrolledTooltip} from 'reactstrap'
import {ReactComponent as Minus} from '../../../assets/icons/minus.svg'
import {ReactComponent as Palm} from '../../../assets/icons/palm.svg'
import {ReactComponent as Plus} from '../../../assets/icons/plus.svg'
import {usePreviewArtworks} from '../../../hooks/usePreviewArtworks'
import {useResponsiveCanvas} from '../../../hooks/useResponsiveCanvas'
import {useSelectedVariantContext} from '../context/SelectedVariantContext'
import {SCALE_TYPE} from '../context/SelectedVariantContext/constants'
import {formatMockupSideName, roundDecimal} from '../context/SelectedVariantContext/helper'
import SwitchBetweenSidesWithHelper from './SwitchBetweenSidesWithHelper'

const MockupAllAreas = ({activeSide, classes}) => {
    const {
        state: {selectedAttribute, editorWrapper},
    } = useSelectedVariantContext()

    const {fetchAreas} = usePreviewArtworks()

    const [loading, setLoading] = useState(false)
    const [areas, setAreas] = useState([])
    const [scale, setScale] = useState(1)
    const [stage, setStage] = useState({
        scale: 1,
        x: 0,
        y: 0,
    })
    const [mockup, setMockup] = useState(null)
    const allAreaRef = useRef(null)
    const stageContainerRef = useRef(null)
    const [stageDrag, setStageDrag] = useState(false)

    // Sử dụng custom hook cho responsive
    const { width: canvasWidth, height: canvasHeight, isResizing } = useResponsiveCanvas(stageContainerRef, 250)

    // Hàm tính toán scale dựa trên container size
    const calculateScale = useCallback((mockupData, containerWidth, containerHeight) => {
        if (!mockupData) return 1
        const scaleX = mockupData.image_width / containerWidth
        const scaleY = mockupData.image_height / containerHeight
        return Math.max(scaleX, scaleY)
    }, [])

    // Effect để update scale khi canvas size thay đổi
    useEffect(() => {
        if (mockup && canvasWidth && canvasHeight) {
            const newScale = calculateScale(mockup, canvasWidth, canvasHeight)
            setScale(newScale)

            // Giữ nguyên vị trí scale zoom của user nếu có
            if (stage.scale !== 1) {
                // Maintain zoom level ratio
                const zoomRatio = stage.scale / scale
                setStage(prevStage => ({
                    ...prevStage,
                    scale: newScale * zoomRatio
                }))
            }
        }
    }, [canvasWidth, canvasHeight, mockup, calculateScale])

    useEffect(() => {
        if (!selectedAttribute?._id || !editorWrapper || activeSide !== 'All') return
        let blob = null

        const fetchArea = async () => {
            try {
                // setLoading(true)
                const areaInfo = await fetchAreas()
                setAreas(areaInfo.artworks)
                const mockupData = areaInfo.mockup.preview['Front']
                setMockup(mockupData)

                // Use canvas size from responsive hook
                const currentWidth = canvasWidth || editorWrapper.width
                const currentHeight = canvasHeight || editorWrapper.height
                const newScale = calculateScale(mockupData, currentWidth, currentHeight)
                setScale(newScale)
            } finally {
                setLoading(false)
            }
        }

        fetchArea()

        return () => URL.revokeObjectURL(blob)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAttribute?._id, activeSide])

    const _handleWheel = (e) => {
        e.evt.preventDefault()
        const scaleBy = 1.1
        const stage = e.target.getStage()
        const oldScale = stage.scaleX()
        const mousePointTo = {
            x: stage.getPointerPosition().x / oldScale - stage.x() / oldScale,
            y: stage.getPointerPosition().y / oldScale - stage.y() / oldScale,
        }

        const newScale = e.evt.deltaY < 0 ? oldScale * scaleBy : oldScale / scaleBy
        if (newScale < 0.2 || newScale > 1) return

        setStage({
            scale: newScale,
            x: (stage.getPointerPosition().x / newScale - mousePointTo.x) * newScale,
            y: (stage.getPointerPosition().y / newScale - mousePointTo.y) * newScale,
        })
    }

    const onScaleStage = (type) => {
        const scaleFactor = type === SCALE_TYPE.MINUS ? 1 / 1.1 : 1.1
        const newScale = stage.scale * scaleFactor
        if (newScale < 0.2 || newScale > 1) return
        const centerWidth = canvasWidth / 2
        const centerHeight = canvasHeight / 2
        setStage({
            scale: newScale,
            x: centerWidth - (centerWidth - stage.x) * scaleFactor, // center X
            y: centerHeight - (centerHeight - stage.y) * scaleFactor, // center Y
        })
    }

    const _onToggleDragStage = () => setStageDrag((prev) => !prev)

    return (
        <div className={classes} ref={allAreaRef} style={{width: '100%', height: '100%', position: 'relative'}}>
            <SwitchBetweenSidesWithHelper />
            {/* {loading && (
                <div className="SpinnerWrapper">
                    <div className="text-primary spinner-border Spinner" role="status">
                        <span className="sr-only">Loading...</span>
                    </div>
                </div>
            )} */}
            <div
                ref={stageContainerRef}
                className={`AllAreaStage ${loading ? 'd-none' : 'd-block'}`}
                style={{
                    width: '100%',
                    height: 'calc(100% - 60px)',
                    position: 'relative',
                    overflow: 'hidden',
                    backgroundColor: '#f5f5f5' // Thêm background màu xám nhạt
                }}
            >
                {mockup && !isResizing && (
                    <Stage
                        x={stage.x}
                        y={stage.y}
                        scaleX={stage.scale}
                        scaleY={stage.scale}
                        draggable={stageDrag}
                        width={canvasWidth}
                        height={canvasHeight}
                        onWheel={_handleWheel}
                        style={{
                            backgroundColor: 'transparent' // Canvas background trong suốt
                        }}
                        onMouseEnter={(e) => {
                            const container = e.target.getStage().container()
                            container.style.cursor = stageDrag ? 'pointer' : 'default'
                        }}
                        onMouseDown={(e) => {
                            const container = e.target.getStage().container()
                            container.style.cursor = stageDrag ? 'grabbing' : 'default'
                        }}
                        onMouseUp={(e) => {
                            const container = e.target.getStage().container()
                            container.style.cursor = stageDrag ? 'pointer' : 'default'
                        }}
                        onMouseLeave={(e) => {
                            const container = e.target.getStage().container()
                            container.style.cursor = 'default'
                        }}
                    >
                        <Layer>
                            <Group
                                width={mockup.image_width / (scale * 2)}
                                height={mockup.image_height / (scale * 2)}
                                x={(canvasWidth - mockup.image_width / scale) / 2}
                                y={(canvasHeight - mockup.image_height / scale) / 2}
                                clipX={0}
                                clipY={0}
                                clipWidth={mockup.image_width / scale}
                                clipHeight={mockup.image_height / scale}
                            >
                                {areas.map((area, index) => {
                                    if (!area) return null
                                    const {src, width, height, x, y, side} = area
                                    const {addition_position = {}} = selectedAttribute?.mockup?.preview_v2?.[side] || {}
                                    const {left = 0, top = 0} = addition_position
                                    return (
                                        <URLImage
                                            key={`${side}-${index}`}
                                            src={src}
                                            side={side}
                                            width={width / scale}
                                            height={height / scale}
                                            x={(left + x) / scale}
                                            y={(top + y) / scale}
                                        />
                                    )
                                })}
                            </Group>
                        </Layer>
                    </Stage>
                )}
            </div>

            <div className="MockupBottom">
                <div className="BottomLeft" />
                <div className="BottomRight d-flex">
                    <div
                        id="dragStage"
                        className={classNames('MockupDrag me-2', {Active: stageDrag})}
                        onClick={_onToggleDragStage}
                    >
                        <Palm width={20} height={20} />
                    </div>
                    <div className="MockupZoom">
                        <InputGroup>
                            <Button
                                id="btn_minus"
                                className="btn-minus"
                                color="default"
                                onClick={() => onScaleStage(SCALE_TYPE.MINUS)}
                            >
                                <Minus width={16} height={16} />
                            </Button>
                            <Input
                                className="TextRatio"
                                type="text"
                                value={`${roundDecimal(stage.scale * 100)}%`}
                                readOnly
                            />
                            <Button
                                id="btn_plus"
                                className="btn-plus"
                                color="default"
                                onClick={() => onScaleStage(SCALE_TYPE.PLUS)}
                            >
                                <Plus width={16} height={16} />
                            </Button>
                        </InputGroup>
                    </div>
                    <UncontrolledTooltip target="dragStage">Hand tool</UncontrolledTooltip>
                    <UncontrolledTooltip target="btn_minus">Zoom out</UncontrolledTooltip>
                    <UncontrolledTooltip target="btn_plus">Zoom in</UncontrolledTooltip>
                </div>
            </div>
        </div>
    )
}

class URLImage extends Component {
    state = {
        image: null,
    }

    componentDidMount() {
        this.loadImage()
    }

    componentDidUpdate(oldProps) {
        if (oldProps.src !== this.props.src) {
            this.loadImage()
        }
    }

    componentWillUnmount() {
        this.image.removeEventListener('load', this.handleLoad)
    }

    loadImage() {
        this.image = new window.Image()
        this.image.src = this.props.src
        this.image.addEventListener('load', this.handleLoad)
    }

    handleLoad = () => {
        this.setState({
            image: this.image,
        })
    }

    renderLayers = () => {
        let {x, y, width, height, side} = this.props
        if (side.includes('neck') || side.includes('yoke')) {
            y -= 60
        } else if (side.includes('neck_right') || side.includes('hood') || side.includes('pocket')) {
            y -= 60
        } else if (side.includes('neck_left')) {
            y -= 20
        } else if (side.includes('hem') || side.includes('cuff')) {
            y += 60
        }

        return (
            <>
                {this.state.image && (
                    <Image
                        x={x}
                        y={y}
                        width={width}
                        height={height}
                        image={this.state.image}
                        ref={(node) => {
                            this.imageNode = node
                        }}
                    />
                )}
                <Text
                    x={x + (width - side.length * 10) / 2}
                    y={y - 20}
                    text={formatMockupSideName(side)}
                    fontSize={16}
                    fill="#333333"
                    fontStyle="bold"
                />
            </>
        )
    }

    render() {
        return <>{this.renderLayers()}</>
    }
}

export default MockupAllAreas
